import { formatCurrency, formatDecimalUnit, formatDecimalWithGroupedUnit } from '@/common/utils/formatters'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import {
  CreateGovernmentAllocationDistributionQuantityValidationSchema,
  CreateGovernmentAllocationDistributionValidationSchema,
} from '@/features/privateCompanies/schemas/createGovernmentAllocationDistribution.schema'
import {
  ControlledByType,
  GovernmentAllocationAllowedCategoriesAndProducts,
  GovernmentAllocationAllowedCategoryAndProductData,
} from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { UseFormSetError } from 'react-hook-form'

type FormSchema =
  | CreateGovernmentAllocationDistributionValidationSchema
  | CreateGovernmentAllocationDistributionQuantityValidationSchema

interface ValidateDistributionLimitsParams {
  form: FormSchema
  allowedCategoriesAndProducts: GovernmentAllocationAllowedCategoriesAndProducts
  controlledBy: ControlledByType
  setError: UseFormSetError<FormSchema>
}

const getValueAndConsumed = (
  distribution: {
    total_value?: string | null
    total_quantity?: string | null
    products_distributions?: any[]
  },
  allowed: GovernmentAllocationAllowedCategoryAndProductData,
  isQuantity: boolean,
) => {
  const formValue = isQuantity
    ? unFormatUnknownUnit(distribution?.total_quantity || '0')
    : unFormatCurrency(distribution?.total_value || '0')

  const consumed = Number(isQuantity ? allowed.consumed_quantity : allowed.consumed_total) || 0

  const formattedConsumed = isQuantity
    ? !!allowed.unit_for_quantity
      ? formatDecimalUnit(consumed, allowed.unit_for_quantity)
      : formatDecimalWithGroupedUnit(consumed, distribution?.products_distributions || [], (p) => p.product.unit_for_quantity)
    : formatCurrency(consumed)

  return { formValue, consumed, formattedConsumed }
}

const getFieldName = (isQuantity: boolean) => (isQuantity ? 'total_quantity' : 'total_value')

const getErrorMessage = (isQuantity: boolean, formattedConsumed: string) =>
  isQuantity
    ? `Quantidade não pode ser menor que o consumido (${formattedConsumed})`
    : `Valor não pode ser menor que o consumido (${formattedConsumed})`

export const validateDistributionLimits = ({
  form,
  allowedCategoriesAndProducts,
  controlledBy,
  setError,
}: ValidateDistributionLimitsParams) => {
  const isQuantity = controlledBy === 'quantity'
  let isValid = true

  const productCategoriesDistributions = form.product_categories_distributions || []

  productCategoriesDistributions.forEach((categoryDistribution, categoryIndex) => {
    const categoryId = categoryDistribution.product_category?.id
    const allowedCategory = allowedCategoriesAndProducts?.categories?.[categoryId]

    if (!allowedCategory) return

    const { formValue, consumed, formattedConsumed } = getValueAndConsumed(
      categoryDistribution,
      allowedCategory,
      isQuantity,
    )

    if (formValue < consumed) {
      const fieldName = `product_categories_distributions.${categoryIndex}.${getFieldName(isQuantity)}`
      setError(fieldName as any, { message: getErrorMessage(isQuantity, formattedConsumed) })
      isValid = false
    }

    const productsDistributions = categoryDistribution.products_distributions || []

    productsDistributions.forEach((productDistribution, productIndex) => {
      const productId = productDistribution.product?.id
      const allowedProduct = allowedCategoriesAndProducts?.products?.[productId]

      if (!allowedProduct) return

      const { formValue, consumed, formattedConsumed } = getValueAndConsumed(
        productDistribution,
        allowedProduct,
        isQuantity,
      )

      if (formValue < consumed) {
        const fieldName = `product_categories_distributions.${categoryIndex}.products_distributions.${productIndex}.${getFieldName(isQuantity)}`
        setError(fieldName as any, { message: getErrorMessage(isQuantity, formattedConsumed) })
        isValid = false
      }
    })
  })

  return isValid
}
